import dayjs from 'dayjs';
import querystring from 'query-string';

import { CLIENT_API_FEVER_HOST } from 'config/envVariable';
import * as clientErrTypes from 'config/clientErrorTypes';
import { loginTypesForApi } from 'config/loginTypes';
import { normalizeResponse } from 'api/common';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';

const TOKEN_EXPIRED_MINUTES = 2;
// buffer for api call
const EXPIRED_BUFFER_MINUTES = 1;
const GET_ACCESS_TOKEN_URL = `https://${CLIENT_API_FEVER_HOST}`;

const tokenManager = () => {
    let accessToken = null;
    let expireTime = null;
    let loginSetting = {};

    const init = config => {
        loginSetting = config;
    };

    // Create long term login token via API
    const generateLoginToken = async () => {
        const resp = await longTermLogin();
        if (resp.httpStatus !== 200) {
            throw generateApiErrObj(resp, clientErrTypes.LOGIN_LONG_TERM_FAILED);
        }
        return true;
    };

    // Remove long term login token via API
    const removeLoginToken = async () => {
        const result = await longTermLogout();
        return result;
    };

    // Set internal access token value
    const setPrivateAccessToken = token => {
        accessToken = token;
        expireTime = dayjs().add(TOKEN_EXPIRED_MINUTES - EXPIRED_BUFFER_MINUTES, 'minute');
    };

    // Get entry token from instagram private link and query string (just use in instagram module)
    const getEntryToken = () => {
        const qs = querystring.parse(window.location.search);
        return qs['entry-token'];
    };

    // Get access token or get with refresh access token
    const getAccessToken = async () => {
        // NOTE: 在參加流程時，若是 IG 模組，不需登入打 API 拿 Token，而是從網址拿 Entry Token，當成 Access Token，去打 API
        if (loginSetting.login_type === loginTypesForApi.INSTAGRAM) {
            let entryToken = getEntryToken();
            // NOTE: 第一次進 ig 活動頁網址會有 entry token，但換頁時，url 會變，所以會遺失 token，前端會存 entry token 到 session storage，若再次點擊卡片進入遊戲至少 session 有保留 token
            if (!Boolean(entryToken)) {
                entryToken = sessionStorage.getItem('entry-token');
            }
            return { token: entryToken, loginType: loginSetting.login_type };
        } else if (dayjs().isBefore(dayjs(expireTime))) {
            return { token: accessToken };
        } else {
            const accessTokenResp = await getAccessTokenAPI(loginSetting);
            if (accessTokenResp.httpStatus === 200) {
                const { token } = accessTokenResp.payload;
                setPrivateAccessToken(token);
                return { token };
            } else {
                accessTokenResp.isGetAccessTokenFailed = true;
                return { errorResp: accessTokenResp };
            }
        }
    };

    // Remove both login token and access token
    const logout = async () => {
        const result = await removeLoginToken();
        if (result === 'success') {
            accessToken = null;
            expireTime = null;
            return true;
        }
        return false;
    };

    return {
        init,
        generateLoginToken,
        getAccessToken,
        getEntryToken,
        logout
    };
};

export default tokenManager();

function longTermLogin() {
    return fetch(`${GET_ACCESS_TOKEN_URL}/auth/client/login`, {
        method: 'POST',
        credentials: 'include'
    }).then(normalizeResponse);
}

async function longTermLogout() {
    const result = await fetch(`${GET_ACCESS_TOKEN_URL}/auth/client/logout`, {
        method: 'POST',
        credentials: 'include'
    }).then(r => (r.status === 200 ? 'success' : 'fail'));
    return result;
}

function getAccessTokenAPI(loginConfig = {}) {
    const qs = querystring.stringify(loginConfig);
    return fetch(`${GET_ACCESS_TOKEN_URL}/auth/client/token${qs && `?${qs}`}`, {
        credentials: 'include'
    }).then(normalizeResponse);
}

export function loginByLiffTokenApi({ accessToken, idToken, sponsorUUID }) {
    return fetch(`${GET_ACCESS_TOKEN_URL}/auth/client/line/liff`, {
        method: 'POST',
        body: JSON.stringify({
            access_token: accessToken,
            id_token: idToken,
            sponsor_uuid: sponsorUUID
        }),
        headers: new Headers({ 'content-type': 'application/json' }),
        credentials: 'include'
    }).then(normalizeResponse);
}

export async function getLogInInfoAPI({ loginType, fbAppId }) {
    return await fetch(
        `${GET_ACCESS_TOKEN_URL}/auth/client/userinfo?login_type=${loginType}$&app_id=${fbAppId}`,
        {
            credentials: 'include'
        }
    ).then(r => {
        if (r.status === 200) {
            return r.json();
        }
        return { status: 'fail' };
    });
}
